<template>
    <b-container v-if="!this.loading" fluid id="videos" class="page">
        <div class="title">
            <h1>Videos</h1>
        </div>
        <div class="mb-5 d-flex justify-content-between align-items-center flex-wrap">
            <b-button-group id="playlist-filter">
                <b-button v-for="(playlist, index) in playlistIDs" :key="playlist.id" variant="light"
                    @click="selectPlaylist(index)" :class="{ active: playlistIndex === index }">
                    {{ playlist.title }}
                </b-button>
            </b-button-group>
            <b-button-group id="video-filter">
                <b-button id="" v-on:click="selectOrder('Desc')" variant="light" value="Desc"
                    :class="{ active: order === 'Desc' }">Desc</b-button>
                <b-button id="" v-on:click="selectOrder('Asc')" variant="light" value="Asc"
                    :class="{ active: order === 'Asc' }">Asc</b-button>
            </b-button-group>
        </div>
        <b-row>
            <b-col v-for="video in sortedVideos" :key="video.id" lg="4" md="6" sm="12" class="mb-4">
                <VideoCard :video_data="video" @play="openPlayer"></VideoCard>
            </b-col>
        </b-row>

        <div v-if="showPlayer" class="yt-lightbox" @click.self="closePlayer">
            <div class="player-box" style="width: min(90vw, calc(90vh * 4 / 3)); max-width: 1200px;">
                <div class="embed-responsive embed-responsive-16by9">
                    <div class="embed-responsive-item">
                        <iframe class="embed-responsive-item"
                            :src="`https://www.youtube.com/embed/${activeVideoId}?autoplay=0`"
                            title="YouTube video player" frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowfullscreen referrerpolicy="strict-origin-when-cross-origin"></iframe>
                    </div>
                </div>
            </div>
        </div>
    </b-container>
    <loading class="loading-animation" v-else></loading>
</template>

<script>
import VideoCard from "@/components/VideoCard.vue";
import Loading from "@/components/Loading.vue";


const API_Key = "AIzaSyC4 - _q7wLqdGzUIG7oPMKudvcGowXENu1k"
const VCMI_Channel_Id = "UC_Mm30jeZDQnvPq_NlPvOpQ"

export default {
    name: "Videos",
    components: {
        VideoCard,
        Loading,
    },
    props: ["page"],
    data() {
        return {
            loading: true,
            error: null,
            channelTitle: null,
            playlistID: null,
            order: 'Desc',
            playlistIDs: [],
            playlistIndex: 0,
            videos: [],
            maxVideos: 500,
            fetchAllOnMount: true,
            showPlayer: false,
            activeVideoId: null
        };
    },
    computed: {
        sortedVideos() {
            const arr = [...this.videos]
            arr.sort((a, b) => new Date(a.publishedAt) - new Date(b.publishedAt))
            return this.order === 'Asc' ? arr : arr.reverse()
        }
    },
    mounted() {
        this.getVideos()
        this.$nextTick(() => {
            this.handleButtonGroup();
        });
    },
    methods: {
        formatDate(iso) {
            try {
                return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(iso))
            } catch {
                return iso
            }
        },
        openPlayer(id) {
            this.activeVideoId = id
            this.showPlayer = true
            document.documentElement.style.overflow = 'hidden' // lock scroll behind overlay
        },
        closePlayer() {
            this.showPlayer = false
            this.activeVideoId = null
            document.documentElement.style.overflow = '' // restore scroll
        },
        selectOrder(newOrder) {
            this.order = newOrder
            this.videos.reverse()
        },
        selectPlaylist(newPlaylistIndex) {
            this.playlistIndex = newPlaylistIndex
            this.getVideos()
        },
        async fetchPlaylistIds(channelId, apiKey) {
            const customOrder = ["VCMI Invited Talks", "VCMI Talks", "VCMI Journal Club"]

            const url = new URL('https://www.googleapis.com/youtube/v3/playlists')
            url.searchParams.set('part', 'snippet, contentDetails')
            url.searchParams.set('channelId', channelId)
            url.searchParams.set('key', apiKey)


            const res = await fetch(url.toString())
            if (!res.ok) throw new Error(`Failed to fetch playlists (${res.status})`)
            const data = await res.json()
            let response = []
            for (let index in data.items) {
                let playlist = data.items[index]
                if (playlist.contentDetails.itemCount != 0) {
                    response.push({ id: playlist.id, title: playlist.snippet.title })
                }
            }

            response.sort((a, b) => {
                return customOrder.indexOf(a.title) - customOrder.indexOf(b.title)
            })

            return response
        },
        async fetchPlaylistPage(playlistId, apiKey, pageToken) {
            const url = new URL('https://www.googleapis.com/youtube/v3/playlistItems')
            url.searchParams.set('part', 'snippet')
            url.searchParams.set('playlistId', playlistId)
            url.searchParams.set('maxResults', '50')
            if (pageToken) url.searchParams.set('pageToken', pageToken)
            url.searchParams.set('key', apiKey)


            const res = await fetch(url.toString())
            if (!res.ok) throw new Error(`Failed to fetch playlist items (${res.status})`)
            return res.json()
        },

        async getVideos() {
            try {
                this.loading = true
                this.error = null
                this.videos = []

                if (!VCMI_Channel_Id || !API_Key) {
                    throw new Error('Missing YouTube API configuration. Set YT_CHANNEL_ID and YT_API_KEY in the script.')
                }

                if (this.playlistIDs.length === 0) {
                    this.playlistIDs = await this.fetchPlaylistIds(VCMI_Channel_Id, API_Key)
                }

                const currentPlaylist = this.playlistIDs[this.playlistIndex]
                if (!currentPlaylist) throw new Error("No playlist selected.")

                let next
                let total = 0
                do {
                    const videos_data = await this.fetchPlaylistPage(currentPlaylist.id, API_Key, next)
                    if (videos_data != []) {
                        for (const video of videos_data.items) {
                            const video_info = video.snippet
                            if (!video_info || !video_info.resourceId.videoId) continue
                            this.videos.push({
                                id: video_info.resourceId.videoId,
                                title: video_info.title,
                                description: video_info.description,
                                publishedAt: video_info.publishedAt,
                                thumbnail: video_info.thumbnails.high.url || video_info.thumbnails.medium.url || video_info.thumbnails.default.url || ''
                            })
                            total++
                            if (total >= this.maxTotal && this.fetchAllOnMount) break
                        }

                        next = videos_data.nextPageToken && this.fetchAllOnMount ? videos_data.nextPageToken : undefined
                    }
                } while (next && total < this.maxTotal)

            } catch (e) {
                this.error = e.message || 'Something went wrong fetching videos.'
                console.error(e)
            } finally {
                this.loading = false
            }
        }
    },
};
</script>

<style lang="scss">
.loading-animation {
    position: relative;
    transform: scale(0.6);
}

.yt-lightbox {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .85);
    z-index: 2000;
    display: grid;
    place-items: center;
    padding: 4vw;
}

.lightbox-close {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 2001;
}
</style>